import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Slider from '@mui/material/Slider';
import React, { FC, useCallback, useMemo, useState } from 'react';
import CropperComponent from 'react-easy-crop';
import { type Area, useVideoCrop } from 'src/hooks/useVideoCrop';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import styles from './VideoCropModal.module.scss';

interface VideoCropModalProps {
  videoFile: File;
  open: boolean;
  onConfirm: (croppedFile: File) => void;
  onCancel: () => void;
}

type CropPoint = {
  x: number;
  y: number;
};

type CropperComponentProps = {
  video: string;
  crop: CropPoint;
  zoom: number;
  aspect: number;
  onCropChange: (crop: CropPoint) => void;
  onZoomChange: (zoom: number) => void;
  onCropComplete: (croppedArea: Area, croppedAreaPixels: Area) => void;
};

const Cropper =
  CropperComponent as unknown as React.ComponentType<CropperComponentProps>;

export const VideoCropModal: FC<VideoCropModalProps> = ({
  videoFile,
  open,
  onConfirm,
  onCancel,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const { cropVideo, cancelCrop, isProcessing, progress } = useVideoCrop();

  const videoUrl = useMemo(() => URL.createObjectURL(videoFile), [videoFile]);

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const handleConfirm = async () => {
    if (!croppedAreaPixels) {
      return;
    }

    try {
      const croppedFile = await cropVideo(videoFile, croppedAreaPixels);
      onConfirm(croppedFile);
    } catch {
      // Cancelled or failed — do nothing
    }
  };

  const handleCancel = () => {
    cancelCrop();
    onCancel();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Crop video</DialogTitle>
      <DialogContent>
        <div className={styles.content}>
          <p className={styles.description}>
            The video must be in vertical format (9:16). Select the crop area
            below.
          </p>

          <div className={styles.cropContainer}>
            <Cropper
              video={videoUrl}
              crop={crop}
              zoom={zoom}
              aspect={9 / 16}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />

            {isProcessing && (
              <div className={styles.progressOverlay}>
                <MaterialCircularProgress
                  size={40}
                  height="inherit"
                  message=""
                />
                <span className={styles.progressText}>
                  Cropping video... {progress}%
                </span>
              </div>
            )}
          </div>

          <div className={styles.zoomControl}>
            <span className={styles.zoomLabel}>Zoom</span>
            <Slider
              className={styles.zoomSlider}
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(_, value) => setZoom(value as number)}
              disabled={isProcessing}
              size="small"
            />
          </div>

          <div className={styles.actions}>
            <button className={styles.cancelButton} onClick={handleCancel}>
              Cancel
            </button>
            <button
              className={styles.confirmButton}
              onClick={handleConfirm}
              disabled={isProcessing || !croppedAreaPixels}
            >
              {isProcessing ? 'Processing...' : 'Confirm'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
